import { useEffect } from "react";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/admin/ProtectedRoute";

import Header from "./components/Header";
import Footer from "./components/Footer";

import Home from "./pages/Home";

// ABOUT
import VisionMissionMandate from "./pages/about/VisionMissionMandate";
import OGFZAHistory from "./pages/about/OGFZAHistory";
import TrackRecord from "./pages/about/TrackRecord";
import WhyOGFZ from "./pages/about/WhyOGFZ";
import ManagementTeam from "./pages/about/ManagementTeam";
import OGFZAOrganogram from "./pages/about/OGFZAOrganogram";
import Legal from "./pages/about/Legal";
import MDMessage from "./pages/about/MDMessage";
import Incentives from "./pages/about/Incentives";
import ClientOverview from "./pages/about/ClientOverview";

// SPV
import AboutSPV from "./pages/spv/AboutSPV";
import ContactSPV from "./pages/spv/ContactSPV";

// FREE ZONES
import RegistrationProcedure from "./pages/free-zones/RegistrationProcedure";
import Onne from "./pages/free-zones/Onne";
import NotoreIndustrialCity from "./pages/free-zones/NotoreIndustrialCity";
import Brawal from "./pages/free-zones/Brawal";
import BESTAF from "./pages/free-zones/BESTAF";
import EkoSupport from "./pages/free-zones/EkoSupport";
import Liberty from "./pages/free-zones/Liberty";
import SPFLFZE from "./pages/free-zones/SPFLFZE";
import Brass from "./pages/free-zones/Brass";
import Warri from "./pages/free-zones/Warri";
import SecuredBunkeringAnchorage from "./pages/free-zones/SecuredBunkeringAnchorage";
import OrashiSpecialEnergy from "./pages/free-zones/OrashiSpecialEnergy";

// SERVICES
import RegistrationOfFreeZoneEnterprise from "./pages/services/registry/RegistrationOfFreeZoneEnterprise";
import RenewalOfLicence from "./pages/services/registry/RenewalOfLicence";
import ApplicationForLicence from "./pages/services/operations/ApplicationForLicence";
import SubmissionOfAnnualReturns from "./pages/services/operations/SubmissionOfAnnualReturns";
import ServiceCharter from "./pages/services/hse/ServiceCharter";
import Engineering from "./pages/services/engineering/Engineering";

// PORTAL
import Login from "./pages/portal/Login";
import RegistryPortal from "./pages/portal/RegistryPortal";
import ProtectedPortalRoute from "./pages/portal/ProtectedPortalRoute";

// PUBLICATIONS
import OGFZANews from "./pages/publications/OGFZANews";
import Announcement from "./pages/publications/Announcement";
import Newsletters from "./pages/publications/Newsletters";
import OGFZADownload from "./pages/publications/OGFZADownload";
import EaseOfBusinessServiceCharter from "./pages/publications/EaseOfBusinessServiceCharter";
import MediaGallery from "./pages/publications/MediaGallery";

// DOWNLOADS
import OGFZAAct1996 from "./pages/downloads/OGFZAAct1996";
import OGFZARegulations2019 from "./pages/downloads/OGFZARegulations2019";
import FreeZoneApplicationForm from "./pages/downloads/FreeZoneApplicationForm";
import MemoAndArticlesOfAssociation from "./pages/downloads/MemoAndArticlesOfAssociation";
import ParticularsOfDirectors from "./pages/downloads/ParticularsOfDirectors";
import StatementOfShareCapital from "./pages/downloads/StatementOfShareCapital";
import OGFZABOIDisclosureForm from "./pages/downloads/OGFZABOIDisclosureForm";
import DisclosureOfBeneficialOwnersRegulation from "./pages/downloads/DisclosureOfBeneficialOwnersRegulation";
import ClientListDownload from "./pages/downloads/ClientListDownload";

// CONTACT
import FAQ from "./pages/contact/FAQ";
import ClientServiceLines from "./pages/contact/ClientServiceLines";
import CustomerService from "./pages/contact/CustomerService";

// ADMIN
import AdminSigninPage from "./pages/admin/AdminSigninPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";

import AdminPosts from "./pages/admin/posts/AdminPosts";
import AdminAddPost from "./pages/admin/posts/AdminAddPost";
import AdminEditPost from "./pages/admin/posts/AdminEditPost";

import AdminCategories from "./pages/admin/categories/AdminCategories";
import AdminTags from "./pages/admin/tags/AdminTags";
import AdminComments from "./pages/admin/comments/AdminComments";
import AdminMedia from "./pages/admin/media/AdminMedia";
import AdminUsers from "./pages/admin/users/AdminUsers";
import AdminNewsletter from "./pages/admin/newsletter/AdminNewsletter";
import AdminContacts from "./pages/admin/contacts/AdminContacts";
import AdminSEOSettings from "./pages/admin/seo/AdminSEOSettings";
import AdminProjects from "./pages/admin/projects/AdminProjects";
import AdminAddProject from "./pages/admin/projects/AdminAddProject";
import AdminEditProject from "./pages/admin/projects/AdminEditProject";
import AdminProjectCategories from "./pages/admin/projects/AdminProjectCategories";
import AdminComplaints from "./pages/admin/complaints/AdminComplaints";
import AdminComplaintDetails from "./pages/admin/complaints/AdminComplaintDetails";
import AdminPartnerships from "./pages/admin/partnerships/AdminPartnerships";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const WebsiteLayout = ({ children }) => (
  <>
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </>
);

const PublicPage = ({ children }) => <WebsiteLayout>{children}</WebsiteLayout>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              borderRadius: "14px",
              fontWeight: "600",
            },
          }}
        />

        <Routes>
          {/* ================= PUBLIC WEBSITE ================= */}

          <Route
            path="/"
            element={
              <PublicPage>
                <Home />
              </PublicPage>
            }
          />

          {/* ABOUT */}
          <Route
            path="/about"
            element={
              <PublicPage>
                <VisionMissionMandate />
              </PublicPage>
            }
          />
          <Route
            path="/about/vision-mission-mandate"
            element={
              <PublicPage>
                <VisionMissionMandate />
              </PublicPage>
            }
          />
          <Route
            path="/about/history"
            element={
              <PublicPage>
                <OGFZAHistory />
              </PublicPage>
            }
          />
          <Route
            path="/about/track-record"
            element={
              <PublicPage>
                <TrackRecord />
              </PublicPage>
            }
          />
          <Route
            path="/about/why-ogfz"
            element={
              <PublicPage>
                <WhyOGFZ />
              </PublicPage>
            }
          />
          <Route
            path="/about/management-team"
            element={
              <PublicPage>
                <ManagementTeam />
              </PublicPage>
            }
          />
          <Route
            path="/about/organogram"
            element={
              <PublicPage>
                <OGFZAOrganogram />
              </PublicPage>
            }
          />
          <Route
            path="/about/legal"
            element={
              <PublicPage>
                <Legal />
              </PublicPage>
            }
          />
          <Route
            path="/about/md-message"
            element={
              <PublicPage>
                <MDMessage />
              </PublicPage>
            }
          />
          <Route
            path="/about/incentives"
            element={
              <PublicPage>
                <Incentives />
              </PublicPage>
            }
          />
          <Route
            path="/about/client-overview"
            element={
              <PublicPage>
                <ClientOverview />
              </PublicPage>
            }
          />

          {/* SPV */}
          <Route
            path="/spv"
            element={
              <PublicPage>
                <AboutSPV />
              </PublicPage>
            }
          />
          <Route
            path="/spv/about"
            element={
              <PublicPage>
                <AboutSPV />
              </PublicPage>
            }
          />
          <Route
            path="/spv/contact"
            element={
              <PublicPage>
                <ContactSPV />
              </PublicPage>
            }
          />

          {/* FREE ZONES */}
          <Route
            path="/free-zones"
            element={
              <PublicPage>
                <RegistrationProcedure />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/registration-procedure"
            element={
              <PublicPage>
                <RegistrationProcedure />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/onne"
            element={
              <PublicPage>
                <Onne />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/notore-industrial-city"
            element={
              <PublicPage>
                <NotoreIndustrialCity />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/brawal"
            element={
              <PublicPage>
                <Brawal />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/bestaf"
            element={
              <PublicPage>
                <BESTAF />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/eko-support"
            element={
              <PublicPage>
                <EkoSupport />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/liberty"
            element={
              <PublicPage>
                <Liberty />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/spfl-fze"
            element={
              <PublicPage>
                <SPFLFZE />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/brass"
            element={
              <PublicPage>
                <Brass />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/warri"
            element={
              <PublicPage>
                <Warri />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/sba"
            element={
              <PublicPage>
                <SecuredBunkeringAnchorage />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/secured-bunkering-anchorage"
            element={
              <PublicPage>
                <SecuredBunkeringAnchorage />
              </PublicPage>
            }
          />
          <Route
            path="/free-zones/orashi-special-energy"
            element={
              <PublicPage>
                <OrashiSpecialEnergy />
              </PublicPage>
            }
          />

          {/* SERVICES */}
          <Route
            path="/services"
            element={
              <PublicPage>
                <RegistrationOfFreeZoneEnterprise />
              </PublicPage>
            }
          />
          <Route
            path="/services/registry/registration-of-free-zone-enterprise"
            element={
              <PublicPage>
                <RegistrationOfFreeZoneEnterprise />
              </PublicPage>
            }
          />
          <Route
            path="/services/registry/renewal-of-licence"
            element={
              <PublicPage>
                <RenewalOfLicence />
              </PublicPage>
            }
          />
          <Route
            path="/services/operations/application-for-licence"
            element={
              <PublicPage>
                <ApplicationForLicence />
              </PublicPage>
            }
          />
          <Route
            path="/services/operations/submission-of-annual-returns"
            element={
              <PublicPage>
                <SubmissionOfAnnualReturns />
              </PublicPage>
            }
          />
          <Route
            path="/services/hse/service-charter"
            element={
              <PublicPage>
                <ServiceCharter />
              </PublicPage>
            }
          />
          <Route
            path="/services/engineering"
            element={
              <PublicPage>
                <Engineering />
              </PublicPage>
            }
          />

          {/* PORTAL - ISOLATED ROUTES WITHOUT WEBSITE HEADER/FOOTER */}
          <Route path="/portal" element={<Navigate to="/portal/login" replace />} />

          <Route path="/portal/login" element={<Login />} />

          <Route
            path="/portal/registry-portal"
            element={
              <ProtectedPortalRoute>
                <RegistryPortal />
              </ProtectedPortalRoute>
            }
          />

          <Route
            path="/portal/fms"
            element={
              <ProtectedPortalRoute>
                <RegistryPortal />
              </ProtectedPortalRoute>
            }
          />

          {/* PUBLICATIONS */}
          <Route
            path="/publications"
            element={
              <PublicPage>
                <Announcement />
              </PublicPage>
            }
          />
          <Route
            path="/publications/news"
            element={
              <PublicPage>
                <OGFZANews />
              </PublicPage>
            }
          />
          <Route
            path="/publications/announcement"
            element={
              <PublicPage>
                <Announcement />
              </PublicPage>
            }
          />
          <Route
            path="/publications/newsletters"
            element={
              <PublicPage>
                <Newsletters />
              </PublicPage>
            }
          />
          <Route
            path="/publications/ogfza-download"
            element={
              <PublicPage>
                <OGFZADownload />
              </PublicPage>
            }
          />
          <Route
            path="/publications/ease-of-business-service-charter"
            element={
              <PublicPage>
                <EaseOfBusinessServiceCharter />
              </PublicPage>
            }
          />
          <Route
            path="/publications/media-gallery"
            element={
              <PublicPage>
                <MediaGallery />
              </PublicPage>
            }
          />

          {/* DOWNLOADS */}
          <Route
            path="/downloads"
            element={
              <PublicPage>
                <OGFZAAct1996 />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/ogfza-act-1996"
            element={
              <PublicPage>
                <OGFZAAct1996 />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/ogfza-regulations-2019"
            element={
              <PublicPage>
                <OGFZARegulations2019 />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/free-zone-application-form"
            element={
              <PublicPage>
                <FreeZoneApplicationForm />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/memo-and-articles-of-association"
            element={
              <PublicPage>
                <MemoAndArticlesOfAssociation />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/particulars-of-directors"
            element={
              <PublicPage>
                <ParticularsOfDirectors />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/statement-of-share-capital"
            element={
              <PublicPage>
                <StatementOfShareCapital />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/ogfza-boi-disclosure-form"
            element={
              <PublicPage>
                <OGFZABOIDisclosureForm />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/disclosure-of-beneficial-owners-regulation"
            element={
              <PublicPage>
                <DisclosureOfBeneficialOwnersRegulation />
              </PublicPage>
            }
          />
          <Route
            path="/downloads/client-list-download"
            element={
              <PublicPage>
                <ClientListDownload />
              </PublicPage>
            }
          />

          {/* CONTACT */}
          <Route
            path="/contact"
            element={
              <PublicPage>
                <FAQ />
              </PublicPage>
            }
          />
          <Route
            path="/contact/faq"
            element={
              <PublicPage>
                <FAQ />
              </PublicPage>
            }
          />
          <Route
            path="/contact/client-service-lines"
            element={
              <PublicPage>
                <ClientServiceLines />
              </PublicPage>
            }
          />
          <Route
            path="/contact/customer-service"
            element={
              <PublicPage>
                <CustomerService />
              </PublicPage>
            }
          />

          {/* ================= ADMIN AUTH ================= */}

          <Route path="/admin-signin" element={<AdminSigninPage />} />

          {/* ================= ADMIN ROUTES - DO NOT REMOVE ================= */}

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />

            <Route path="posts" element={<AdminPosts />} />
            <Route path="posts/add" element={<AdminAddPost />} />
            <Route path="posts/edit/:id" element={<AdminEditPost />} />

            <Route path="categories" element={<AdminCategories />} />
            <Route path="tags" element={<AdminTags />} />
            <Route path="comments" element={<AdminComments />} />

            <Route path="media" element={<AdminMedia />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="seo" element={<AdminSEOSettings />} />

            <Route path="projects" element={<AdminProjects />} />
            <Route path="projects/add" element={<AdminAddProject />} />
            <Route path="projects/edit/:id" element={<AdminEditProject />} />
            <Route
              path="projects/categories"
              element={<AdminProjectCategories />}
            />

            <Route path="complaints" element={<AdminComplaints />} />
            <Route path="complaints/:id" element={<AdminComplaintDetails />} />

            <Route path="partnerships" element={<AdminPartnerships />} />
          </Route>

          {/* ================= 404 ================= */}

          <Route
            path="*"
            element={
              <PublicPage>
                <VisionMissionMandate />
              </PublicPage>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;